<script>
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
  name: 'FerrySeasonPriceCabin',
  props: {
    shown: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      headers: [
        { text: 'Name', sortable: false, class: 'px-2', width: '10%', value: 'name' },
        { text: 'Gross', sortable: false, class: 'px-2', width: '35%', value: 'gross' },
        { text: 'Net', sortable: false, class: 'px-2', width: '35%', value: 'net' },
        { text: 'Delete', sortable: false, class: 'px-2', width: '10%', value: '' },
        { text: 'State', sortable: false, class: 'px-2', width: '10%', value: '' },
      ],
      numberRules: [v => !!v || isNaN(v) || 'Input has to be a number'],
      seasonPriceCabinsValid: false,
      pricesAltered: [],
      pricesToDelete: [],
    };
  },
  computed: {
    ...mapState({
      departure: state => state.transportation.prices.season.selectedDeparture,
      loading: state => state.transportation.departures.cabins.loading,
      error: state => state.transportation.departures.cabins.error,
    }),
    ...mapGetters({
      prices: 'transportation/getDepartureCabinsWithPrices',
    }),
    deleteButtonString() {
      if (this.pricesToDelete.length) {
        return this.pricesToDelete.length > 1
          ? `Delete ${this.pricesToDelete.length} items`
          : `Delete ${this.pricesToDelete.length} item`;
      }
      return 'Delete';
    },
    saveButtonString() {
      if (this.pricesAltered.length) {
        return this.pricesAltered.length > 1
          ? `Save ${this.pricesAltered.length} items`
          : `Save ${this.pricesAltered.length} item`;
      }
      return 'Save';
    },
  },
  methods: {
    ...mapActions({
      updateDeparturePriceCabin: 'transportation/updateDeparturePriceCabin',
      deleteDeparturePriceCabin: 'transportation/deleteDeparturePriceCabin',
    }),
    updatePriceDelete(priceItem) {
      const priceIndex = this.pricesToDelete.indexOf(priceItem.id);
      if (priceIndex === -1) {
        this.pricesToDelete.push(priceItem.id);
      } else {
        this.pricesToDelete.splice(priceIndex, 1);
      }
    },
    updatePrice(priceItem, change) {
      const priceIndex = this.pricesAltered.findIndex(price => price.entityId === priceItem.entityId);
      if (priceIndex === -1) {
        this.pricesAltered.push({
          ...priceItem,
          ...change.param,
        });
      } else {
        const entries = Object.entries(change.param)[0];
        this.pricesAltered[priceIndex][entries[0]] = entries[1];
      }
    },
    getPriceState(priceItem) {
      if (!priceItem.entityId) {
        return 'NEW';
      }
      return this.pricesAltered.findIndex(price => price.entityId === priceItem.entityId) ? '' : 'EDITED';
    },
    saveItems() {
      const items = this.pricesAltered.map(price => ({
        id: price.id,
        entityId: price.entityId,
        grossPrice: parseInt(price.grossPrice, 10),
        netPrice: parseInt(price.netPrice, 10),
      }));

      this.updateDeparturePriceCabin({ seasonDepartureId: this.departure.id, body: items });
      this.pricesAltered = [];
      this.pricesToDelete = [];
    },
    deleteItems() {
      this.deleteDeparturePriceCabin({ seasonDepartureId: this.departure.id, body: this.pricesToDelete });
      this.pricesAltered = [];
      this.pricesToDelete = [];
    },
    clearAndClose() {
      this.pricesAltered = [];
      this.pricesToDelete = [];
      this.$emit('close');
    },
  },
};
</script>

<template>
  <v-card
    v-if="shown"
  >
    <v-card-title class="pb-1">
      <span class="headline">
        Cabins for "{{ departure.name }}"
      </span>
    </v-card-title>
    <v-card-text class="py-0 mb-3">
      <v-container grid-list-md pt-1>
        <v-form
          ref="seasonPriceCabins"
          v-model="seasonPriceCabinsValid"
          lazy-validation
        >
          <v-data-table
            :items="prices"
            :headers="headers"
            :loading="loading"
            hide-actions
          >
            <template v-slot:no-data>
              <tr v-if="!error && !loading">
                <td colspan="5" class="text-xs-center">No results found</td>
              </tr>
              <tr v-if="error && !loading">
                <td colspan="5" class="text-xs-center">Error</td>
              </tr>
            </template>
            <template v-slot:items="data">
              <td class="px-2">
                {{ data.item.cabinName }}
              </td>
              <td class="px-2">
                <v-text-field
                  :value="data.item.grossPrice"
                  :rules="numberRules"
                  type="number"
                  single-line
                  @change="updatePrice(data.item, {param: {grossPrice: $event}})"
                />
              </td>
              <td class="px-2">
                <v-text-field
                  :value="data.item.netPrice"
                  :rules="numberRules"
                  type="number"
                  single-line
                  @change="updatePrice(data.item, {param: {netPrice: $event}})"
                />
              </td>
              <td class="px-2">
                <v-checkbox
                  v-if="data.item.entityId !== null"
                  @change="updatePriceDelete(data.item, $event)"
                />
              </td>
              <td
                :class="{'warning--text': getPriceState(data.item) === 'EDITED'}"
                class="light-blue--text px-2"
              >
                {{ getPriceState(data.item) }}
              </td>
            </template>
          </v-data-table>
        </v-form>
      </v-container>
    </v-card-text>
    <v-card-actions>
      <v-spacer/>
      <v-btn
        :disabled="!pricesAltered.length || !seasonPriceCabinsValid"
        color="success"
        flat
        @click="saveItems"
      >
        {{ saveButtonString }}
      </v-btn>
      <v-btn
        :disabled="!pricesToDelete.length"
        color="error"
        flat
        @click="deleteItems"
      >
        {{ deleteButtonString }}
      </v-btn>
      <v-btn
        color="blue darken-1"
        flat
        @click="clearAndClose"
      >
        Close
      </v-btn>
    </v-card-actions>
  </v-card>
</template>



// WEBPACK FOOTER //
// src/components/transportation/TransportationDeparturePriceCabin.vue